import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import PromoBanner from "@/components/PromoBanner";
import KeyboardSoundToggle from "@/components/KeyboardSoundToggle";
import { keyClickHandler } from "@/lib/keyboardSound";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Copy, Play, Trophy, Users, Timer, Crown, Medal, Zap } from "lucide-react";

const POLL_MS = 1500;

function accuracyOf(original, typed) {
  if (!typed) return 0;
  const n = Math.min(original.length, typed.length);
  if (n === 0) return 0;
  let correct = 0;
  for (let i = 0; i < n; i++) if (original[i] === typed[i]) correct++;
  return Math.round((correct / typed.length) * 1000) / 10;
}

function podiumIcon(pos) {
  if (pos === 1) return <Crown className="w-5 h-5 text-brand-gold" />;
  if (pos === 2) return <Medal className="w-5 h-5 text-muted-foreground" />;
  if (pos === 3) return <Medal className="w-5 h-5 text-brand-crimson" />;
  return null;
}

export default function RaceRoom() {
  const { code } = useParams();
  const { user } = useAuth();
  const nav = useNavigate();
  const [race, setRace] = useState(null);
  const [typed, setTyped] = useState("");
  const [submittedFinish, setSubmittedFinish] = useState(false);
  const startedAtRef = useRef(null);
  const lastReportRef = useRef({ chars: -1, ts: 0 });
  const onKey = useMemo(() => keyClickHandler(), []);

  // Poll race state
  useEffect(() => {
    let alive = true;
    let timer;
    const tick = async () => {
      try {
        const { data } = await api.get(`/races/${code}`);
        if (!alive) return;
        setRace(data);
        if (data.state === "racing" && !startedAtRef.current) {
          startedAtRef.current = Date.now();
        }
      } catch (e) {
        // silent
      } finally {
        if (alive) timer = setTimeout(tick, POLL_MS);
      }
    };
    tick();
    return () => { alive = false; clearTimeout(timer); };
  }, [code]);

  const isHost = race?.host_user_id === user?.id;
  const me = race?.participants?.find((p) => p.user_id === user?.id);
  const original = race?.passage_text || "";
  const done = me?.finished_at || (typed.length >= original.length && original.length > 0);

  // Progress reporting — throttled to ~1s, always after finish
  const reportProgress = async (typedText, finishNow = false) => {
    if (!race || race.state !== "racing") return;
    if (submittedFinish) return;
    const now = Date.now();
    if (!finishNow && now - lastReportRef.current.ts < 900) return;
    if (!finishNow && lastReportRef.current.chars === typedText.length) return;
    lastReportRef.current = { chars: typedText.length, ts: now };
    const elapsed = Math.max(1, (now - (startedAtRef.current || now)) / 1000);
    const words = typedText.trim().split(/\s+/).filter(Boolean).length;
    const wpm = Math.round((words / elapsed) * 60);
    const acc = accuracyOf(original, typedText);
    try {
      await api.post(`/races/${code}/progress`, {
        chars_typed: typedText.length,
        wpm: Math.min(wpm, 500),
        accuracy: acc,
        finished: finishNow,
      });
      if (finishNow) setSubmittedFinish(true);
    } catch { /* ignore transient errors */ }
  };

  const start = async () => {
    try {
      await api.post(`/races/${code}/start`);
      toast.success("Race started!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not start");
    }
  };

  const onChange = (e) => {
    const v = e.target.value;
    setTyped(v);
    if (v.length >= original.length) {
      reportProgress(v, true);
    } else {
      reportProgress(v);
    }
  };

  const copyCode = () => {
    navigator.clipboard.writeText(code);
    toast.success("Code copied");
  };

  if (!race) {
    return (
      <div className="min-h-screen bg-background text-foreground">
        <Navbar />
        <main className="max-w-4xl mx-auto px-6 py-20 text-center font-mono text-muted-foreground">Loading race…</main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <PromoBanner />
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-10">
        {/* Header */}
        <div className="flex items-start justify-between mb-8 flex-wrap gap-4">
          <div>
            <Link to="/race" className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground hover:text-foreground mb-3 inline-block" data-testid="race-back">
              ← Back to lobby
            </Link>
            <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tight" data-testid="race-title">{race.passage_title}</h1>
            <div className="text-xs font-mono text-muted-foreground mt-1">{race.word_count} words · {race.participants.length}/10 racers</div>
          </div>
          <button onClick={copyCode} className="inline-flex items-center gap-2 h-11 px-4 rounded-lg border border-brand-gold/40 bg-brand-gold/10 font-mono text-2xl font-black tracking-widest text-brand-gold hover:bg-brand-gold/20" data-testid="race-code-display">
            {race.code} <Copy className="w-4 h-4" />
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Main panel */}
          <div className="lg:col-span-2 border border-border rounded-2xl p-6 sm:p-8 bg-card">
            {race.state === "waiting" && (
              <div className="text-center py-10" data-testid="race-waiting">
                <div className="w-16 h-16 border-2 border-brand-gold/40 bg-brand-gold/10 rounded-2xl mx-auto flex items-center justify-center mb-6 animate-pulse-ring">
                  <Users className="w-7 h-7 text-brand-gold" />
                </div>
                <div className="font-display text-2xl font-black mb-2">Waiting for racers…</div>
                <p className="text-muted-foreground mb-6">Share the code <span className="text-brand-gold font-mono font-bold">{race.code}</span> with friends. Minimum 1, up to 10 total.</p>
                {isHost ? (
                  <Button onClick={start} className="h-12 px-8 rounded-lg bg-brand-gold hover:bg-brand-gold/90 text-background text-xs uppercase tracking-[0.2em] font-black glow-gold" data-testid="race-start-btn">
                    <Play className="w-4 h-4 mr-2" /> Start race
                  </Button>
                ) : (
                  <div className="text-xs font-mono text-muted-foreground">Only the host can start. Hang tight.</div>
                )}
              </div>
            )}

            {race.state === "racing" && (
              <div data-testid="race-active">
                <div className="flex items-center justify-between mb-4">
                  <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-muted-foreground">Type as fast as you can</div>
                  <KeyboardSoundToggle />
                </div>
                <div className="border border-border rounded-lg bg-secondary/40 p-5 mb-4 max-h-56 overflow-y-auto">
                  <p className="font-mono text-base leading-loose whitespace-pre-wrap" data-testid="race-source-text">{original}</p>
                </div>
                <Textarea
                  value={typed}
                  onChange={onChange}
                  onKeyDown={onKey}
                  disabled={done}
                  autoFocus
                  rows={7}
                  placeholder="Start typing…"
                  className="font-mono text-base leading-relaxed bg-background border-border rounded-lg"
                  data-testid="race-typing-area"
                />
                {done && !me?.finished_at && (
                  <div className="mt-3 text-sm font-mono text-brand-emerald">✓ Finished — waiting for results…</div>
                )}
              </div>
            )}

            {race.state === "finished" && (
              <div className="text-center py-6" data-testid="race-finished">
                <Trophy className="w-14 h-14 text-brand-gold mx-auto mb-4" />
                <div className="font-display text-3xl font-black tracking-tight mb-2">Race over!</div>
                <p className="text-muted-foreground mb-8">
                  {race.winner_user_id === user?.id ? "🎉 You won this race — legendary!" : `${race.participants.find((p) => p.user_id === race.winner_user_id)?.name || "Someone"} took the gold.`}
                </p>
                <div className="flex justify-center gap-4 flex-wrap">
                  <Link to="/race">
                    <Button className="rounded-lg bg-brand-gold hover:bg-brand-gold/90 text-background text-xs uppercase tracking-[0.2em] font-bold h-11 px-6" data-testid="race-newrace-btn">
                      <Zap className="w-4 h-4 mr-2" /> Race again
                    </Button>
                  </Link>
                  <Link to="/dashboard">
                    <button className="h-11 px-6 border border-border rounded-lg text-xs uppercase tracking-widest font-bold hover:bg-secondary">
                      Dashboard
                    </button>
                  </Link>
                </div>
              </div>
            )}
          </div>

          {/* Leaderboard */}
          <aside className="border border-border rounded-2xl p-6 bg-card" data-testid="race-leaderboard">
            <div className="text-[10px] uppercase tracking-[0.3em] font-bold text-brand-gold mb-4 flex items-center gap-2">
              <Timer className="w-3.5 h-3.5" /> Live standings
            </div>
            <ul className="space-y-2">
              {race.participants.map((p) => {
                const pct = original.length > 0 ? Math.min(100, (p.chars_typed / original.length) * 100) : 0;
                const isMe = p.user_id === user?.id;
                return (
                  <li key={p.user_id} className={`border rounded-lg p-3 ${isMe ? "border-brand-gold/50 bg-brand-gold/5" : "border-border bg-secondary/30"}`} data-testid={`race-rank-${p.position}`}>
                    <div className="flex items-center gap-2 mb-1.5">
                      <div className="w-6 text-center font-mono font-bold text-sm text-muted-foreground">{p.position}</div>
                      {podiumIcon(p.position)}
                      <div className="flex-1 min-w-0 font-bold truncate text-sm">{p.name}{isMe && <span className="text-brand-gold text-xs ml-1">(you)</span>}</div>
                      {p.finished_at && <div className="text-[10px] uppercase tracking-widest font-bold text-brand-emerald">Done</div>}
                    </div>
                    <div className="h-1.5 bg-secondary rounded-full overflow-hidden">
                      <div className="h-full bg-brand-gold transition-all" style={{ width: `${pct}%` }} />
                    </div>
                    <div className="flex justify-between mt-1.5 text-[10px] font-mono text-muted-foreground">
                      <span>{p.wpm || 0} WPM</span>
                      <span>{p.accuracy || 0}% acc</span>
                    </div>
                  </li>
                );
              })}
            </ul>
          </aside>
        </div>
      </main>
    </div>
  );
}
